import { useEffect, useState } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import { SkeletonText, cn } from '@taki/ui';
import type { Inspection } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import { Choix, raisonDe, sac } from './morceaux.js';
import type { OptionDeclaree } from './morceaux.js';

/**
 * LES EXAMENS COMPLEMENTAIRES ET LEUR CONCLUSION (demande du fondateur le
 * 26/10/2026 : cocher « Prise de sang » doit permettre d'en noter la
 * conclusion, en attendant mieux).
 *
 * Le choix des examens reste le champ `extraExams` de la consultation, que la
 * porte declaree lit deja. Chaque examen coche qui recoit une conclusion
 * devient une `inspection` de type `complementaire` (l'examen nomme, sa
 * conclusion), rattachee a la consultation et a l'animal : la meme famille que
 * l'examen clinique, et la place ou ses valeurs chiffrees (une prise de sang)
 * se rattacheront plus tard, comme les constantes. Decocher un examen archive
 * sa fiche : elle se restaure.
 */
export function Complementaires({
  sdk,
  orgId,
  consultationId,
  assetId,
  options,
  valeur,
  onChoisir,
}: {
  sdk: TakiSdk;
  orgId: string;
  consultationId: string;
  assetId: string;
  options: readonly OptionDeclaree[];
  /** Le champ `extraExams` de la consultation. */
  valeur: unknown;
  onChoisir: (valeur: string | string[] | null) => void;
}): JSX.Element {
  const [fiches, setFiches] = useState<readonly Inspection[]>([]);
  const [lues, setLues] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => {
    let vivant = true;
    setLues(false);
    void sdk.inspections.list({ type: 'complementaire', where: { 'customFields.consultationId': consultationId }, limit: 50 }).then(
      (liste) => {
        if (!vivant) return;
        setFiches(liste);
        setLues(true);
      },
      (cause: unknown) => {
        if (!vivant) return;
        setErreur(`Conclusions illisibles : ${raisonDe(cause)}`);
        setLues(true);
      },
    );
    return () => {
      vivant = false;
    };
  }, [sdk, consultationId]);

  const coches = Array.isArray(valeur) ? (valeur as unknown[]).filter((v): v is string => typeof v === 'string') : [];
  const ficheDe = (examen: string): Inspection | undefined => fiches.find((f) => f.archivedAt == null && sac(f)['examen'] === examen);

  /* Decocher un examen archive sa fiche ; le choix lui-meme part au dossier. */
  const choisir = (suivant: string | string[] | null): void => {
    const restants = Array.isArray(suivant) ? suivant : [];
    for (const f of fiches) {
      if (f.archivedAt == null && !restants.includes(String(sac(f)['examen'] ?? ''))) {
        void sdk.inspections.archive(f.id).then(
          () => setFiches((avant) => avant.filter((x) => x.id !== f.id)),
          (cause: unknown) => setErreur(`Conclusion non retirée : ${raisonDe(cause)}`),
        );
      }
    }
    onChoisir(suivant);
  };

  const ecrireConclusion = async (examen: string, texte: string): Promise<void> => {
    const conclusion = texte.trim();
    const existante = ficheDe(examen);
    if (conclusion === String(sac(existante)['conclusion'] ?? '')) return;
    try {
      if (existante !== undefined) {
        const nouvelle = await sdk.inspections.update(existante.id, { customFields: { conclusion: conclusion === '' ? null : conclusion } });
        setFiches((avant) => avant.map((f) => (f.id === nouvelle.id ? nouvelle : f)));
      } else if (conclusion !== '') {
        const nouvelle = await sdk.inspections.create({
          orgId,
          type: 'complementaire',
          ...(assetId === '' ? {} : { assetId }),
          performedAt: new Date(),
          customFields: { consultationId, examen, conclusion },
        });
        setFiches((avant) => [...avant, nouvelle]);
      }
      setErreur(null);
    } catch (cause) {
      setErreur(`Conclusion non enregistrée : ${raisonDe(cause)}`);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <Choix libelle="Demandés ou faits" options={options} valeur={valeur} multiple onEcrire={choisir} />
      {erreur !== null ? <p className="text-sm text-danger">{erreur}</p> : null}
      {coches.length > 0 && !lues ? <SkeletonText lines={2} /> : null}
      {coches.length === 0 || !lues ? null : (
        <div className="flex flex-col gap-2">
          {coches.map((examen) => (
            <Conclusion
              key={`${consultationId}-${examen}`}
              examen={examen}
              valeur={String(sac(ficheDe(examen))['conclusion'] ?? '')}
              onEcrire={(texte) => void ecrireConclusion(examen, texte)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Une ligne : le nom de l'examen, sa conclusion en une phrase, enregistree a Entree ou en quittant le champ. */
function Conclusion({ examen, valeur, onEcrire }: { examen: string; valeur: string; onEcrire: (texte: string) => void }): JSX.Element {
  const [texte, setTexte] = useState(valeur);
  useEffect(() => setTexte(valeur), [valeur]);
  const ecrire = (): void => {
    if (texte.trim() !== valeur.trim()) onEcrire(texte);
  };
  return (
    <label className="flex flex-wrap items-center gap-3">
      <span className="w-44 shrink-0 text-sm font-semibold text-ink-900">{examen}</span>
      <input
        className={cn(
          'h-9 min-w-48 flex-1 rounded-sm border border-border bg-surface-1 px-3 text-sm text-ink-900 placeholder:text-ink-300',
          'focus-visible:border-accent-border focus-visible:outline-none',
        )}
        value={texte}
        placeholder="Conclusion : hématocrite normal, pas d'anémie"
        onChange={(e) => setTexte(e.target.value)}
        onBlur={ecrire}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') ecrire();
        }}
      />
    </label>
  );
}
